import { config } from "@/config/env";
import { getToken, clearToken } from "@/auth/token-store";
import { getTenantId } from "@/tenant/tenant-context";

/**
 * Structured API error with HTTP status code and backend error code.
 */
export class ApiError extends Error {
  /**
   * Creates an ApiError.
   *
   * Args:
   *   status: HTTP status code.
   *   code: Backend error code string.
   *   details: Optional structured error details from the backend.
   */
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly details: Record<string, unknown> | null,
  ) {
    super(`API Error ${status}: ${code}`);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * If true, returns the raw Response object instead of parsed JSON.
   * Used for file downloads where the caller needs blob() access.
   */
  rawResponse?: boolean;
}

/**
 * Core fetch wrapper that injects:
 * - Authorization: Bearer <token> header from in-memory token store
 * - X-Tenant-ID: <tenantId> header from tenant context module accessor
 * - Content-Type: application/json for requests with a body
 * - Structured ApiError for non-2xx responses
 * - 401 auto-logout via custom DOM event
 *
 * Args:
 *   path: API path (e.g., "/api/v1/health"). Combined with config.apiBaseUrl.
 *   options: Request options including method, body, params, and headers.
 *
 * Returns:
 *   Parsed JSON response typed as T, or raw Response if rawResponse is true.
 *
 * Raises:
 *   ApiError: For non-2xx HTTP responses.
 */
export async function apiClient<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, params, headers = {}, signal, rawResponse } = options;

  // Build URL with query params
  const baseUrl = config.apiBaseUrl || window.location.origin;
  const url = new URL(path, baseUrl);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    }
  }

  // Only inject auth/tenant headers for same-origin requests to prevent
  // credential leakage if download_url points to an external service (e.g., S3)
  const expectedOrigin = new URL(baseUrl).origin;
  const isSameOrigin = url.origin === expectedOrigin;

  const requestHeaders: Record<string, string> = {
    ...headers,
  };

  if (isSameOrigin) {
    const token = getToken();
    const tenantId = getTenantId();

    if (token) {
      requestHeaders["Authorization"] = `Bearer ${token}`;
    }
    if (tenantId) {
      requestHeaders["X-Tenant-ID"] = tenantId;
    }
  }
  if (body !== undefined) {
    requestHeaders["Content-Type"] = "application/json";
  }

  const response = await fetch(url.toString(), {
    method,
    headers: requestHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  // Handle 401 — clear token and dispatch logout event
  if (response.status === 401) {
    clearToken();
    window.dispatchEvent(new CustomEvent("auth:logout"));
    throw new ApiError(401, "unauthorized", null);
  }

  // Handle raw response for downloads
  if (rawResponse) {
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({
        error: "unknown",
        message: response.statusText,
        details: null,
      })) as { error?: string; message?: string; details?: Record<string, unknown> | null };
      throw new ApiError(response.status, errorBody.error ?? "unknown", errorBody.details ?? null);
    }
    return response as unknown as T;
  }

  // 204 No Content — return undefined
  if (response.status === 204) {
    return undefined as T;
  }

  // Parse JSON response
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({
      error: "unknown",
      message: response.statusText,
      details: null,
    })) as { error?: string; message?: string; details?: Record<string, unknown> | null };
    throw new ApiError(
      response.status,
      errorBody.error ?? errorBody.message ?? "unknown",
      errorBody.details ?? null,
    );
  }

  return response.json() as Promise<T>;
}
